import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Eye, Pencil, Trash2 } from 'lucide-react'
import { teamMembersApi, TeamMember, User, Team } from '../../lib/api/teamMembers'
import { Badge } from '../../components/common'

export const Route = createFileRoute('/dashboard/team-members/')({
  component: TeamMembersComponent,
})

function TeamMembersComponent() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null)
  const queryClient = useQueryClient()

  // Get current user info
  const { data: currentUserData } = useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      const response = await fetch('/api/v1/current_user', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
      return response.json()
    }
  })

  const currentUser = currentUserData?.user
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'owner'

  // Fetch team members
  const { data, isLoading, error } = useQuery({
    queryKey: ['teamMembers'],
    queryFn: () => teamMembersApi.getAll(),
  })

  // Fetch users for selection
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: teamMembersApi.getUsers,
  })

  // Fetch teams for selection
  const { data: teamsData } = useQuery({
    queryKey: ['teams'],
    queryFn: teamMembersApi.getTeams,
  })

  // Get default team (first team)
  const defaultTeam = teamsData?.teams?.[0]

  // Create mutation
  const createMutation = useMutation({
    mutationFn: teamMembersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] })
      setIsModalOpen(false)
      setEditingMember(null)
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      teamMembersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] })
      setIsModalOpen(false)
      setEditingMember(null)
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: teamMembersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] })
    },
  })

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data = {
      user_id: Number(formData.get('user_id')),
      team_id: defaultTeam?.id || Number(formData.get('team_id')),
      role: formData.get('role') as string,
      status: formData.get('status') as string,
    }

    if (editingMember) {
      updateMutation.mutate({ id: editingMember.id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const handleEdit = (member: TeamMember) => {
    setEditingMember(member)
    setIsModalOpen(true)
  }

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to remove this team member?')) {
      deleteMutation.mutate(id)
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingMember(null)
  }

  // Filter users who have at least one skill
  const usersWithSkills = usersData?.users.filter(user => user.user_skills && user.user_skills.length > 0) || []

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Team Members</h1>
            <p className="text-base-content/70 mt-1">
              Manage team members and assignments
            </p>
          </div>
          {isAdmin && (
            <button
              className="btn btn-accent text-white"
              onClick={() => setIsModalOpen(true)}
            >
              Add Team Member
            </button>
          )}
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <p className="text-center py-8">Loading team members...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="alert alert-error">
            <span>Error: {error.message}</span>
          </div>
        )}

        {/* Team Members Grid */}
        {data && data.team_members && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.team_members.map((member) => (
              <div key={member.id} className="card bg-base-100 shadow-xl">
                <div className="card-body">
                  <h2 className="card-title">{member.user.full_name || member.user.email}</h2>
                  <p className="text-sm text-base-content/70">{member.user.email}</p>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Badge variant={member.status === 'active' ? 'success' : 'warning'}>
                        {member.status}
                      </Badge>
                      <Badge outline>{member.role}</Badge>
                    </div>
                    <div>
                      <p className="text-sm font-semibold mb-1">Skills:</p>
                      <div className="flex flex-wrap gap-1">
                        {member.user.skills && member.user.skills.length > 0 ? (
                          member.user.skills.map((skill) => (
                            <Badge key={skill.id} variant="accent" size="sm">
                              {skill.name}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-base-content/50">No skills assigned</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {isAdmin && (
                    <div className="card-actions justify-end mt-4">
                      <Link
                        to="/dashboard/team-members/$id"
                        params={{ id: String(member.id) }}
                        className="btn btn-sm btn-ghost"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() => handleEdit(member)}
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        className="btn btn-sm btn-ghost text-error"
                        onClick={() => handleDelete(member.id)}
                        disabled={deleteMutation.isPending}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {data && data.team_members && data.team_members.length === 0 && (
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <p className="text-center text-base-content/50 py-8">
                No team members found. Click "Add Team Member" to get started!
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">
              {editingMember ? 'Edit Team Member' : 'Add Team Member'}
            </h3>

            {!defaultTeam && (
              <div className="alert alert-error mb-4">
                <span>
                  ⚠️ No team found. Please run database seeds: <code className="text-xs">bin/rails db:seed</code>
                </span>
              </div>
            )}

            {!editingMember && usersWithSkills.length === 0 && (
              <div className="alert alert-warning mb-4">
                <span>
                  ⚠️ No users with skills found. Users must have at least one skill before being added as team members.
                </span>
              </div>
            )}

            {(createMutation.error || updateMutation.error) && (
              <div className="alert alert-error mb-4">
                <span>
                  {(createMutation.error as any)?.response?.data?.errors?.join(', ') || 'An error occurred'}
                </span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {!editingMember && (
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">User (must have skills) *</span>
                  </label>
                  <select
                    name="user_id"
                    className="select select-bordered"
                    required
                    disabled={usersWithSkills.length === 0}
                  >
                    <option value="">Select a user</option>
                    {usersWithSkills.map((user: User) => (
                      <option key={user.id} value={user.id}>
                        {user.email} ({user.user_skills?.length || 0} skills)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {editingMember && (
                <div className="alert alert-info text-white">
                  <span>
                    Editing: <strong>{editingMember.user.full_name || editingMember.user.email}</strong>
                  </span>
                </div>
              )}

              {/* Hidden team field - auto-assigned to default team */}
              {defaultTeam && (
                <div className="alert alert-info text-sm text-white">
                  <span>
                    Team: <strong>{defaultTeam.name}</strong> (auto-assigned)
                  </span>
                </div>
              )}

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Role *</span>
                </label>
                <select
                  name="role"
                  className="select select-bordered"
                  required
                  defaultValue={editingMember?.role || 'member'}
                >
                  <option value="member">Member</option>
                  <option value="lead">Lead</option>
                  <option value="manager">Manager</option>
                </select>
              </div>

              <div className="form-control">
                <label className="label">
                  <span className="label-text">Status *</span>
                </label>
                <select
                  name="status"
                  className="select select-bordered"
                  required
                  defaultValue={editingMember?.status || 'active'}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="modal-action">
                <button
                  type="button"
                  className="btn"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-accent text-white"
                  disabled={createMutation.isPending || updateMutation.isPending || (!editingMember && usersWithSkills.length === 0) || !defaultTeam}
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'Saving...'
                    : editingMember
                    ? 'Update'
                    : 'Add'}
                </button>
              </div>
            </form>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button onClick={handleCloseModal}>close</button>
          </form>
        </dialog>
      )}
    </>
  )
}
